const SECTION_SELECTOR = '[data-uc-psp-clinicals]';
const READY_CLASS = 'is-cards-ready';
const RESET_CLASS = 'is-cards-reset';
const NUDGE_DELAY = 520;
const NUDGE_MS = 420;
const NUDGE_DISTANCE = 20;
const SLIDE_SPEED = 700;
// Wheel handling mirrors the prototype: a flick adds up to one step, then the
// rest of it (inertia included) is swallowed until the wheel goes quiet.
const WHEEL_SNAP = 40;
const WHEEL_QUIET_MS = 140;
const WHEEL_RESET_MS = 80;

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const markReached = (swiper) => {
  for (const [i, slide] of swiper.slides.entries()) {
    slide.classList.toggle('is-reached', i <= swiper.activeIndex);
  }
};

const paintSlides = (swiper) => {
  const head = swiper.progress * Math.max(1, swiper.slides.length - 1);

  // Upcoming slides have negative progress; `ahead` is 1 until a slide starts
  // arriving and 0 once it is active.
  const ahead = (slide) => (slide ? clamp(-slide.progress, 0, 1) : 0);

  for (const [i, slide] of swiper.slides.entries()) {
    const fade = clamp(Math.abs(slide.progress), 0, 1);

    slide.style.setProperty('--psp-slide-fade', `${fade}`);
    slide.style.setProperty('--psp-slide-fill', `${clamp(head - i, 0, 1)}`);
    slide.style.setProperty('--psp-slide-ahead', `${ahead(slide)}`);
    slide.style.setProperty('--psp-next-ahead', `${ahead(swiper.slides[i + 1])}`);
  }
};

const setSlideDuration = (swiper, duration) => {
  for (const slide of swiper.slides) {
    slide.style.setProperty('--psp-slide-duration', `${duration}ms`);

    const fill = slide.querySelector('.psp-clinicals__fill');
    if (fill) fill.style.transitionDuration = `${duration}ms`;
  }
};

const wheelDeltaX = (event, box) => {
  let { deltaX: x, deltaY: y } = event;

  if (event.deltaMode === 1) {
    x *= 16;
    y *= 16;
  } else if (event.deltaMode === 2) {
    x *= box.clientWidth;
    y *= box.clientHeight;
  }

  if (event.shiftKey && Math.abs(x) < Math.abs(y)) return y;
  return Math.abs(x) >= Math.abs(y) * 1.25 ? x : 0;
};

const bindWheel = (panel, getSlider) => {
  let accum = 0;
  let locked = false;
  let resetTimer = 0;
  let unlockTimer = 0;

  const hold = () => {
    locked = true;
    accum = 0;
    clearTimeout(unlockTimer);
    unlockTimer = setTimeout(() => {
      locked = false;
      accum = 0;
    }, WHEEL_QUIET_MS);
  };

  panel.addEventListener(
    'wheel',
    (event) => {
      const x = wheelDeltaX(event, panel);
      if (Math.abs(x) < 1) return;

      event.preventDefault();

      if (locked) {
        hold();
        return;
      }

      accum += x;
      clearTimeout(resetTimer);
      resetTimer = setTimeout(() => {
        accum = 0;
      }, WHEEL_RESET_MS);

      const slider = getSlider();
      if (!slider || Math.abs(accum) < WHEEL_SNAP) return;

      const next = slider.activeIndex + Math.sign(accum);
      if (next < 0 || next > slider.slides.length - 1) return;

      slider.slideTo(next);
      hold();
    },
    { passive: false }
  );
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const whenSwiperReady = (callback) => {
  if (window.Swiper) {
    callback();
    return;
  }

  let attempts = 0;
  const timer = setInterval(() => {
    attempts += 1;

    if (window.Swiper) {
      clearInterval(timer);
      callback();
    } else if (attempts > 100) {
      clearInterval(timer);
    }
  }, 50);
};

const initSection = (root) => {
  if (root.dataset.ucPspInit) return;
  root.dataset.ucPspInit = '1';

  const panels = Array.from(root.querySelectorAll('[data-uc-psp-panel]'));
  if (panels.length === 0) return;

  const tabs = Array.from(root.querySelectorAll('[data-uc-psp-tab]'));
  const cta = root.querySelector('[data-uc-psp-cta]');
  const indicator = root.querySelector('[data-uc-psp-indicator]');
  const frame = root.querySelector('.psp-clinicals__frame');
  const header = root.querySelector('[data-uc-psp-tabs]');

  let active = 0;

  const clampIndex = (index) => clamp(index, 0, panels.length - 1);

  const studyWrappers = Array.from(root.querySelectorAll('[data-uc-psp-study]'));

  for (const wrapper of studyWrappers) {
    const stale = wrapper.id ? document.getElementById(wrapper.id) : null;
    if (stale && stale !== wrapper) stale.remove();

    document.body.appendChild(wrapper);
  }

  const studyHandle = studyWrappers[0]?.dataset.ucPspStudy ?? '';

  if (cta) {
    if (studyHandle) cta.setAttribute('data-uc-sidebar-trigger', studyHandle);
    else cta.removeAttribute('data-uc-sidebar-trigger');
  }

  let sliders = [];

  const playEnter = (index) => {
    const panel = panels[index];
    if (!panel) return;

    panel.classList.add(RESET_CLASS);
    panel.classList.remove(READY_CLASS);
    void panel.offsetWidth;
    panel.classList.remove(RESET_CLASS);
    requestAnimationFrame(() => panel.classList.add(READY_CLASS));
  };

  sliders = panels.map((panel, index) => {
    const container = panel.querySelector('[data-uc-psp-swiper]');
    if (!container) return null;

    return new Swiper(container, {
      slidesPerView: 'auto',
      centeredSlides: true,
      spaceBetween: 0,
      speed: SLIDE_SPEED,
      breakpoints: { 1024: { allowTouchMove: false } },
      watchSlidesProgress: true,
      keyboard: { enabled: true, onlyInViewport: true },
      on: {
        init: markReached,
        progress: paintSlides,
        setTransition: setSlideDuration,
        slideChange: markReached,
      },
    });
  });

  for (const [index, panel] of panels.entries()) {
    bindWheel(panel, () => sliders[index]);

    panel.addEventListener('click', (event) => {
      const slider = sliders[index];
      const slide = event.target.closest?.('.psp-clinicals__slide');
      if (!slider || !slide) return;

      const slideIndex = Array.from(slider.slides).indexOf(slide);
      if (slideIndex >= 0 && slideIndex !== slider.activeIndex) slider.slideTo(slideIndex);
    });
  }

  const moveIndicator = () => {
    const button = tabs[active];
    if (!indicator || !button) return;

    indicator.style.top = `${button.offsetTop}px`;
    indicator.style.height = `${button.offsetHeight}px`;
    indicator.style.width = `${button.offsetWidth}px`;
    indicator.style.transform = `translateX(${button.offsetLeft}px)`;
  };

  const measure = () => {
    if (frame && header) {
      const offset = header.getBoundingClientRect().bottom - frame.getBoundingClientRect().top;
      root.style.setProperty('--psp-content-top', `${Math.round(offset)}px`);
    }

    moveIndicator();
  };

  const activate = (index) => {
    const next = clampIndex(index);

    if (next === active) {
      moveIndicator();
      return;
    }

    active = next;

    for (const [i, panel] of panels.entries()) {
      panel.classList.toggle('is-active', i === active);
    }

    for (const [i, tab] of tabs.entries()) {
      tab.classList.toggle('is-active', i === active);
      tab.setAttribute('aria-current', i === active ? 'true' : 'false');
    }

    const slider = sliders[active];
    if (slider) {
      slider.update();
      slider.slideTo(0, 0);
    }

    moveIndicator();
    playEnter(active);
  };

  const goToTab = (index) => {
    const next = clampIndex(index);
    if (next === active) return;

    activate(next);
  };

  const nudgeSlides = async () => {
    const slider = sliders[active];
    if (!slider || slider.slides.length < 2 || slider.activeIndex !== 0) return;

    const base = slider.translate;
    slider.setTransition(NUDGE_MS);
    slider.setTranslate(base - NUDGE_DISTANCE);
    await sleep(NUDGE_MS);

    if (slider.activeIndex !== 0) return;
    slider.setTranslate(base);
    await sleep(NUDGE_MS);
  };

  const nudgeIndicator = async () => {
    const [first, second] = tabs;
    if (!indicator || !first || !second || active !== 0) return;

    const shift = second.offsetLeft + second.offsetWidth * 0.5 - (first.offsetLeft + first.offsetWidth);
    indicator.style.transition = `transform ${NUDGE_MS}ms ease`;
    indicator.style.transform = `translateX(${first.offsetLeft + shift}px)`;
    await sleep(NUDGE_MS);

    indicator.style.transform = `translateX(${first.offsetLeft}px)`;
    await sleep(NUDGE_MS);

    indicator.style.transition = '';
    moveIndicator();
  };

  const playHint = async () => {
    await sleep(NUDGE_DELAY);
    if (active !== 0) return;

    await nudgeSlides();
    await nudgeIndicator();
  };

  if (!reduceMotion() && 'IntersectionObserver' in window) {
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;

        observer.disconnect();
        playHint();
      },
      { threshold: 0.5 }
    );

    observer.observe(frame ?? root);
  }

  for (const [index, tab] of tabs.entries()) {
    tab.addEventListener('click', () => goToTab(index));
  }

  window.addEventListener('resize', measure, { passive: true });
  window.addEventListener('orientationchange', measure, { passive: true });
  document.fonts?.ready.then(measure);

  root.addEventListener('shopify:block:select', (event) => {
    const panel = event.target.closest?.('[data-uc-psp-panel]');
    if (!panel) return;

    const index = panels.indexOf(panel);
    if (index >= 0) goToTab(index);

    const slider = sliders[index];
    const slide = event.target.closest?.('.psp-clinicals__slide');
    if (!slide || !slider) return;

    const slideIndex = Array.from(slider.slides).indexOf(slide);
    if (slideIndex >= 0) slider.slideTo(slideIndex);
  });

  panels[0].classList.add('is-active');
  root.dataset.ucPspReady = '1';
  measure();
  playEnter(0);
};

const initAll = (scope) => {
  whenSwiperReady(() => {
    for (const root of scope.querySelectorAll(SECTION_SELECTOR)) {
      initSection(root);
    }
  });
};

initAll(document);

document.addEventListener('shopify:section:load', (event) => {
  initAll(event.target);
});
